import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Clock, User, Activity, Calendar, Users, ArrowUpRight, Plus, Minus } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay, FreeMode } from 'swiper/modules';
import { FloatingShapes } from '../components/FloatingShapes';
import { useBookingModal } from '../contexts/BookingModalContext';
import 'swiper/css';
import 'swiper/css/navigation';

gsap.registerPlugin(ScrollTrigger);

export const HomePage: React.FC = () => {
    const heroRef = useRef<HTMLDivElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const quoteTextRef = useRef<HTMLHeadingElement>(null);
    const iconRef = useRef<HTMLImageElement>(null);
    const mainRef = useRef<HTMLDivElement>(null);
    const [openFaq, setOpenFaq] = useState<number | null>(null);
    const { openModal } = useBookingModal();

    const faqs = [
        {
            question: "How can therapy help me?",
            answer: (
                <>
                    <p className="mb-4">Therapy can be a transformative and empowering experience, providing numerous benefits to individuals seeking emotional, mental, and psychological support. Here are some ways in which therapy can help you:</p>
                    <ul className="list-disc pl-5 space-y-1 mb-4">
                        <li>Improved Emotional Well-being</li>
                        <li>Enhanced Self-Awareness</li>
                        <li>Stress and Anxiety Reduction</li>
                        <li>Improved Relationships</li>
                        <li>Grief and Loss Support</li>
                        <li>Behavioral Changes</li>
                        <li>Increased Self-Esteem and Confidence</li>
                        <li>Resolution of Past Traumas</li>
                        <li>Coping with Life Transitions</li>
                        <li>Better Decision-Making Skills</li>
                        <li>Managing Mental Health Disorders</li>
                        <li>Personal Growth and Development</li>
                    </ul>
                    <p>Remember, each therapy experience is unique, and the benefits you receive may depend on your willingness to engage in the therapeutic process and your relationship with the therapist. It’s essential to find a therapist who is a good fit for you and your specific needs.</p>
                </>
            )
        },

        {
            question: "How much does therapy cost?",
            answer: (
                <>
                    <p className="mb-4">We understand that cost is an important factor when seeking mental health services. We strive to make therapy accessible and affordable for everyone. Our pricing structure is designed to accommodate different financial situations.</p>
                    <p className="mb-4"><strong>For clients with insurance coverage</strong>, we accept multiple insurance companies. The cost of therapy for insured clients may vary depending on their specific insurance plan, co-payments, deductibles, and other factors. We recommend contacting our office or your insurance provider directly to verify your coverage and determine any potential out-of-pocket expenses.</p>
                    <p className="mb-4"><strong>For clients without insurance or who prefer to pay out-of-pocket</strong>, we offer a sliding scale fee system. The sliding scale takes into consideration your income and financial circumstances, allowing us to adjust the cost of therapy accordingly. We believe that everyone should have the opportunity to access quality mental health care, regardless of their financial situation.</p>
                    <p className="mb-4">Please keep in mind that therapy costs may differ based on the type and duration of the sessions, as well as any specialized services or assessments that may be required. To get a specific quote for your therapy needs, we encourage you to reach out to our office directly. Our friendly staff will be happy to discuss your options and provide further information about our fees and payment options.</p>
                    <p>At our practice, we are committed to providing compassionate and effective therapy services while being mindful of your financial needs. Our goal is to create a supportive and inclusive environment where you can focus on your well-being and personal growth.</p>
                </>
            )
        },
        {
            question: "How can I tell if a therapist is a good match for me?",
            answer: (
                <>
                    <p className="mb-4">Finding a therapist who is a good match for you is essential for a successful therapeutic experience. Here are some key factors to consider when determining if a therapist is the right fit for you:</p>
                    <ul className="list-disc pl-5 space-y-1 mb-4">
                        <li>Qualifications and Credentials</li>
                        <li>Approach and Specialization</li>
                        <li>Personal Connection</li>
                        <li>Communication and Listening Skills</li>
                        <li>Flexibility and Open-Mindedness</li>
                        <li>Respect for Boundaries</li>
                        <li>Feedback and Collaboration</li>
                        <li>Accessibility and Availability</li>
                        <li>Cost and Insurance</li>
                        <li>Reviews and Recommendations</li>
                    </ul>
                    <p>Remember that finding the right therapist may take some time and exploration. It’s okay to try a few sessions with different therapists before making a decision. Ultimately, trust your instincts and prioritize your comfort and well-being when selecting a therapist.</p>
                </>
            )
        },
        {
            question: "How long is a session?",
            answer: "A therapy session typically runs for 50-60 minutes. This duration allows sufficient time to explore and address the concerns you bring to the session while maintaining a focused and effective therapeutic experience."
        }
    ];

    useEffect(() => {
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (videoRef.current && prefersReducedMotion) {
            videoRef.current.pause();
        }

        // Skip animations if user prefers reduced motion
        if (prefersReducedMotion) return;

        const ctx = gsap.context(() => {
            // === HEADER ANIMATION ===
            gsap.from("header", {
                opacity: 0,
                y: -50,
                duration: 1,
                ease: "power3.out"
            });

            // === HERO SECTION ===
            const heroTl = gsap.timeline({ defaults: { ease: 'power3.out' } });

            heroTl.from(titleRef.current, {
                opacity: 0,
                y: 50,
                duration: 1.2,
            })
                .from('.hero-tagline', {
                    opacity: 0,
                    x: 50,
                    duration: 1,
                }, '-=0.8')
                .from('.hero-cta', {
                    scale: 0.8,
                    opacity: 0,
                    duration: 0.8,
                    ease: "back.out(1.7)"
                }, '-=0.5')
                .from('.feature-card', {
                    opacity: 0,
                    x: -30,
                    duration: 0.8,
                    stagger: 0.2,
                }, '-=0.5');

            // Rotating icon animation
            if (iconRef.current) {
                gsap.to(iconRef.current, {
                    rotation: 360,
                    duration: 8,
                    repeat: -1,
                    ease: 'none',
                });
            }

            // === QUOTE SECTION ===
            if (quoteTextRef.current) {
                const words = quoteTextRef.current.querySelectorAll('.word');
                gsap.set(words, { opacity: 0, y: 40 });

                gsap.to(words, {
                    opacity: 1,
                    y: 0,
                    duration: 1.2,
                    stagger: 0.15,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: quoteTextRef.current,
                        start: 'top 80%',
                        toggleActions: 'play none none none',
                    },
                });

                // Parallax for quote section
                gsap.to('.quote-section', {
                    yPercent: 15,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: '.quote-section',
                        start: 'top bottom',
                        end: 'bottom top',
                        scrub: 1
                    }
                });
            }

            // === SERVICES CAROUSEL ===
            const serviceCards = document.querySelectorAll('.service-card-wrapper');
            serviceCards.forEach((card) => {
                gsap.from(card, {
                    opacity: 0,
                    y: 50,
                    duration: 0.8,
                    ease: 'power2.out',
                    scrollTrigger: {
                        trigger: card,
                        start: 'top 85%',
                        toggleActions: 'play none none none',
                    }
                });
            });

            // === ABOUT INTRO ===
            gsap.from('.about-intro-image', {
                opacity: 0,
                x: -80,
                duration: 1.2,
                ease: 'power3.out',
                scrollTrigger: {
                    trigger: '.about-intro-image',
                    start: 'top 80%',
                    toggleActions: 'play none none none',
                },
            });

            gsap.from('.about-intro-content', {
                opacity: 0,
                x: 80,
                duration: 1.2,
                ease: 'power3.out',
                scrollTrigger: {
                    trigger: '.about-intro-content',
                    start: 'top 80%',
                    toggleActions: 'play none none none',
                },
            });

            gsap.to('.floating-badge', {
                y: -10,
                duration: 2,
                repeat: -1,
                yoyo: true,
                ease: 'power1.inOut',
            });

            // === FEATURE CARDS ===
            gsap.set('.feature-card-item', { opacity: 1 });
            gsap.from('.feature-card-item', {
                opacity: 0,
                y: 60,
                duration: 1,
                stagger: 0.2,
                ease: 'power2.out',
                scrollTrigger: {
                    trigger: '.feature-card-item',
                    start: 'top 85%',
                    toggleActions: 'play none none none',
                },
            });

            // === DEPRESSION SECTION ===
            // Parallax background
            gsap.fromTo('.depression-section video',
                { yPercent: -20 },
                {
                    yPercent: 0,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: '.depression-section',
                        start: 'top bottom',
                        end: 'bottom top',
                        scrub: true
                    }
                }
            );

            gsap.from('.depression-title', {
                opacity: 0,
                scale: 0.9,
                duration: 1,
                ease: 'power3.out',
                scrollTrigger: {
                    trigger: '.depression-section',
                    start: 'top 80%',
                    toggleActions: 'play none none none',
                }
            });

            gsap.from('.depression-description', {
                opacity: 0,
                y: 30,
                duration: 0.8,
                delay: 0.2,
                ease: 'power2.out',
                scrollTrigger: {
                    trigger: '.depression-section',
                    start: 'top 80%',
                    toggleActions: 'play none none none',
                }
            });

            // === FAQ SECTION ===
            gsap.from('.faq-image', {
                opacity: 0,
                scale: 0.95,
                duration: 1,
                ease: 'power2.out',
                scrollTrigger: {
                    trigger: '.faq-section',
                    start: 'top 80%',
                    toggleActions: 'play none none none',
                }
            });

            gsap.from('.faq-content', {
                opacity: 0,
                x: 50,
                duration: 1,
                ease: 'power2.out',
                scrollTrigger: {
                    trigger: '.faq-section',
                    start: 'top 80%',
                    toggleActions: 'play none none none',
                }
            });

            gsap.from('.faq-item', {
                opacity: 0,
                y: 20,
                duration: 0.6,
                stagger: 0.1,
                ease: 'power2.out',
                scrollTrigger: {
                    trigger: '.faq-content',
                    start: 'top 75%',
                    toggleActions: 'play none none none',
                }
            });

            // === REASSURANCE SECTION ===
            gsap.from('.reassurance-section', {
                opacity: 0,
                y: 50,
                duration: 1,
                ease: 'power2.out',
                scrollTrigger: {
                    trigger: '.reassurance-section',
                    start: 'top 85%',
                    toggleActions: 'play none none none',
                }
            });

            // === FOOTER ===
            gsap.from("footer", {
                opacity: 0,
                duration: 1,
                ease: 'power2.out',
                scrollTrigger: {
                    trigger: "footer",
                    start: 'top 95%',
                    toggleActions: 'play none none none',
                }
            });

            setTimeout(() => {
                ScrollTrigger.refresh();
            }, 1000);

        }, mainRef);

        return () => ctx.revert();
    }, []);

    const services = [
        {
            title: 'Drug And Alcohol Evaluations',
            description: 'Professional assessments to guide your recovery journey.',
            image: '/images/service-eval.jpg'
        },
        {
            title: 'Substance Use Evaluations',
            description: 'Comprehensive understanding of substance use patterns.',
            image: '/images/service-substance.jpg'
        },
        {
            title: 'DUI Education Classes',
            description: 'State-approved education for legal compliance and safety.',
            image: '/images/service-dui.jpg'
        },
        {
            title: 'Individual Therapy',
            description: 'One-on-one sessions focused on your personal growth.',
            image: '/images/therapy-individual.jpg'
        },
        {
            title: 'Couples and Family Therapy',
            description: 'Strengthening relationships and improving communication.',
            image: '/images/therapy-family.jpg'
        },
        {
            title: 'Outpatient Therapy Groups',
            description: 'Supportive community settings for shared healing.',
            image: '/images/therapy-group.jpg'
        },
    ];

    return (
        <div ref={mainRef} className="home-page bg-white overflow-x-hidden max-w-full">
            {/* Hero Section */}
            <section
                ref={heroRef}
                className="relative min-h-screen flex items-center overflow-hidden"
                aria-label="Hero section"
            >
                <video
                    ref={videoRef}
                    className="absolute inset-0 w-full h-full object-cover"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="none"
                    poster="/images/HeroFallback-min.png"
                    aria-hidden="true"
                >
                    <source src="/videos/hero-therapy.mp4" type="video/mp4" />
                    <img
                        src="/images/HeroFallback-min.png"
                        alt=""
                        className="w-full h-full object-cover"
                    />
                </video>

                <div
                    className="absolute inset-0"
                    style={{ background: 'linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5))' }}
                    aria-hidden="true"
                ></div>

                {/* Content - Full width matching header */}
                <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 py-20 h-screen">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 h-full">

                        {/* Left Side - Title and Feature Cards */}
                        <div className="text-white flex flex-col justify-start pt-20">
                            <h1
                                ref={titleRef}
                                className="font-bold text-white leading-tight mb-12"
                                style={{
                                    fontSize: '3rem',
                                    fontWeight: 700
                                }}
                            >
                                Michelle Simmons<br />
                                Counseling Group, PLLC
                            </h1>

                            <div className="space-y-8 hidden md:block">
                                <div className="feature-card flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 backdrop-blur-sm">
                                        <Clock className="w-6 h-6 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg text-white font-bold mb-1">Trauma-Informed Care</h3>
                                        <p className="text-white/80 text-sm max-w-xs">Specialized support that recognizes and understands the impact of trauma.</p>
                                    </div>
                                </div>

                                <div className="feature-card flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 backdrop-blur-sm">
                                        <User className="w-6 h-6 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg text-white font-bold mb-1">Personalized Healing</h3>
                                        <p className="text-white/80 text-sm max-w-xs">Tailored therapeutic approaches designed for your unique journey.</p>
                                    </div>
                                </div>

                                <div className="feature-card flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 backdrop-blur-sm">
                                        <Activity className="w-6 h-6 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg text-white font-bold mb-1">Holistic Growth</h3>
                                        <p className="text-white/80 text-sm max-w-xs">Fostering balance and wellness in all aspects of your life.</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Side - Quote positioned lower */}
                        <div className="text-white relative lg:pl-12 flex items-end pb-0">
                            <div className="space-y-6 w-full">
                                <p
                                    className="hero-tagline font-medium leading-tight text-white text-base sm:text-[2rem]"
                                >
                                    Your journey to complete healing begins here.
                                    We help you find the way.
                                </p>

                                <div className="hero-cta">
                                    <button
                                        onClick={openModal}
                                        className="inline-flex items-center gap-3 bg-white text-black pl-8 pr-2 py-2 rounded-full font-bold text-lg transition-all duration-300 hover:scale-110 shadow-lg hover:shadow-xl group"
                                    >
                                        Book Now
                                        <div className="w-10 h-10 rounded-full bg-[#d68800] text-white flex items-center justify-center transition-all duration-300 group-hover:bg-[#891719] group-hover:rotate-45">
                                            <ArrowUpRight className="w-5 h-5" />
                                        </div>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Section 2: Quote Block with Floating Shapes */}
            <section className="quote-section pt-0 pb-24 relative overflow-hidden bg-white">
                {/* Floating geometric shapes */}
                <FloatingShapes variant="minimal" />

                <div className="w-full px-4 sm:px-6 lg:px-8">
                    {/* Top-left microtext with rotating icon */}
                    <div className="flex items-center gap-3 mb-12">
                        <img
                            ref={iconRef}
                            src="/images/Flower shape.png"
                            alt=""
                            className="w-12 h-12 object-contain"
                        />
                        <p className="text-xs uppercase tracking-widest text-neutral-600 font-semibold">
                            Physical office spaces located in Aurora and Denver.
                        </p>
                    </div>

                    {/* Large quote text - Shorter, more impactful */}
                    <h2
                        ref={quoteTextRef}
                        className="hero-subheadline font-bold text-mscg-dark-gray leading-[1.1]"
                        style={{
                            maxWidth: '1200px',
                            opacity: 1,
                            fontSize: 'clamp(3rem, 6vw, 7rem)' // Reduced font size to fit in 2 lines
                        }}
                    >
                        {/* Content will be split by GSAP into words */}
                        <span className="word inline-block">You</span>{' '}
                        <span className="word inline-block">don't</span>{' '}
                        <span className="word inline-block">have</span>{' '}
                        <span className="word inline-block">to</span>{' '}
                        <span className="word inline-block text-gray-600">figure</span>{' '}
                        <span className="word inline-block text-gray-600">things</span>{' '}
                        <span className="word inline-block text-gray-600">out</span>{' '}
                        <span className="word inline-block">on</span>{' '}
                        <span className="word inline-block">your</span>{' '}
                        <span className="word inline-block">own...</span>
                    </h2>
                </div>
            </section>

            {/* Section 3: Services Carousel */}
            <section className="pb-24 pt-12 bg-white overflow-hidden">
                {/* Text removed per user request */}

                {/* Swiper Carousel */}
                <div className="relative px-4 sm:px-6 lg:px-8 group/carousel">
                    {/* Custom Navigation Buttons */}
                    <button className="swiper-button-prev-custom absolute left-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center rounded-full bg-white border-2 border-[#d68800] text-[#d68800] opacity-0 group-hover/carousel:opacity-100 transition-all duration-300 hover:bg-[#d68800] hover:text-white -ml-4 lg:-ml-12 shadow-lg">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
                    </button>
                    <button className="swiper-button-next-custom absolute right-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center rounded-full bg-white border-2 border-[#d68800] text-[#d68800] opacity-0 group-hover/carousel:opacity-100 transition-all duration-300 hover:bg-[#d68800] hover:text-white -mr-4 lg:-mr-12 shadow-lg">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
                    </button>

                    <Swiper
                        modules={[Navigation, Autoplay, FreeMode]}
                        spaceBetween={30}
                        slidesPerView={1}
                        navigation={{
                            prevEl: '.swiper-button-prev-custom',
                            nextEl: '.swiper-button-next-custom',
                        }}
                        loop={true}
                        speed={6000} // Slow continuous scroll
                        autoplay={{
                            delay: 0,
                            disableOnInteraction: false,
                            pauseOnMouseEnter: true // Pause when hovering to read
                        }}
                        freeMode={true}
                        grabCursor={true}
                        breakpoints={{
                            640: { slidesPerView: 1 },
                            768: { slidesPerView: 2 },
                            1024: { slidesPerView: 3 },
                        }}
                        className="services-swiper py-8"
                    >
                        {services.map((service, index) => (
                            <SwiperSlide key={index}>
                                <div
                                    className="h-[500px] rounded-2xl p-8 transition-all duration-300 relative overflow-hidden group"
                                    style={{
                                        border: '2px solid transparent'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.borderColor = '#d68800';
                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.borderColor = 'transparent';
                                        e.currentTarget.style.boxShadow = 'none';
                                    }}
                                >
                                    {/* Background Image */}
                                    <img
                                        src={service.image}
                                        alt={service.title}
                                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                    />

                                    {/* Dark Overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30"></div>

                                    {/* Content */}
                                    <div className="relative z-10 h-full flex flex-col justify-end text-white">
                                        <h4 className="text-2xl font-bold mb-3 text-white drop-shadow-md">
                                            {service.title}
                                        </h4>
                                    </div>
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            </section>

            {/* About Intro Section */}
            <section className="pb-24 bg-white">
                <div className="w-full px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        {/* Left Side - Image with Floating Badge */}
                        <div className="about-intro-image relative">
                            <div className="relative rounded-3xl overflow-hidden shadow-2xl group/image">
                                <img
                                    src="/images/therapy-session.jpg"
                                    alt="Therapy session at MSCG"
                                    className="w-full h-[500px] object-cover transition-transform duration-700 ease-out group-hover/image:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover/image:opacity-100 transition-opacity duration-500"></div>
                            </div>

                            {/* Floating Badge */}
                            <div className="floating-badge absolute bottom-8 left-8 bg-white rounded-2xl shadow-xl p-6 flex items-center gap-4 max-w-xs hover:shadow-2xl transition-all duration-500 hover:scale-105 cursor-pointer">
                                <div className="flex -space-x-3">
                                    <img
                                        src="/images/NewHeadshots/Denisha Walker Headshot.png"
                                        alt="Team member"
                                        className="w-10 h-10 rounded-full object-cover border-2 border-white transition-all duration-300 hover:scale-125 hover:z-10 hover:border-[#d68800] cursor-pointer"
                                    />
                                    <img
                                        src="/images/NewHeadshots/Emily Palmiotto.jpeg"
                                        alt="Team member"
                                        className="w-10 h-10 rounded-full object-cover border-2 border-white transition-all duration-300 hover:scale-125 hover:z-10 hover:border-[#d68800] cursor-pointer"
                                    />
                                    <img
                                        src="/images/NewHeadshots/Kim Jackson Headshot.jpeg"
                                        alt="Team member"
                                        className="w-10 h-10 rounded-full object-cover border-2 border-white transition-all duration-300 hover:scale-125 hover:z-10 hover:border-[#d68800] cursor-pointer"
                                    />
                                    <div className="w-10 h-10 rounded-full bg-[#d68800] border-2 border-white flex items-center justify-center text-white text-xs font-bold">
                                        10+
                                    </div>
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-mscg-dark-gray">Licensed Therapists</p>
                                    <p className="text-xs text-neutral-500">Serving Colorado</p>
                                </div>
                            </div>
                        </div>

                        {/* Right Side - Content */}
                        <div className="about-intro-content">
                            <h2 className="text-4xl md:text-5xl font-bold text-mscg-dark-gray mb-2 inline-block relative">
                                Your Path to <span className='text-gray-600'>Total</span> Wellness
                                <span className="absolute bottom-0 left-0 w-24 h-1 bg-[#d68800]"></span>
                            </h2>

                            <div className="mt-8 space-y-6 text-neutral-700 leading-relaxed text-lg">
                                <p>
                                    Michelle Simmons Counseling Group, PLLC (MSCG) provides individual, family, and group therapy
                                    in the metropolitan area with physical office spaces located in Aurora and Denver.
                                </p>

                                <p>
                                    We also provide <b>telehealth services</b> statewide.
                                </p>

                                <p>
                                    The goal is to help clients address a variety of mental health conditions including anxiety, depression, and trauma.
                                    We also provide services to address substance misuse on a court-mandated or voluntary basis.
                                </p>
                            </div>

                            <div className="mt-8">
                                <a
                                    href="#/our-team"
                                    className="inline-flex items-center gap-3 bg-[#293039] text-white pl-8 pr-2 py-2 rounded-full font-bold text-lg transition-all duration-300 hover:scale-110 shadow-lg hover:shadow-xl group"
                                >
                                    Meet Our Team
                                    <div className="w-10 h-10 rounded-full bg-[#d68800] text-white flex items-center justify-center transition-all duration-300 group-hover:bg-[#891719] group-hover:rotate-45">
                                        <ArrowUpRight className="w-5 h-5" />
                                    </div>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Section 4: Feature Cards - 4 Column Grid */}
            <section className="pb-32 bg-mscg-light-gray">
                <div className="w-full px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        <div
                            className="feature-card-item p-8 rounded-2xl transition-all duration-300 bg-white"
                            style={{ border: '2px solid transparent' }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#d68800';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = 'transparent';
                                e.currentTarget.style.boxShadow = 'none';
                            }}
                        >
                            <div className="mb-8">
                                <Clock className="w-12 h-12 text-mscg-dark-gray" />
                            </div>
                            <h3 className="text-xl font-bold text-mscg-dark-gray mb-3">
                                Easy Scheduling
                            </h3>
                            <p className="text-neutral-600 leading-relaxed">
                                Get available therapy sessions with flexible scheduling. No long waiting lists.
                            </p>
                        </div>

                        <div
                            className="feature-card-item p-8 rounded-2xl transition-all duration-300 bg-white"
                            style={{ border: '2px solid transparent' }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#d68800';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = 'transparent';
                                e.currentTarget.style.boxShadow = 'none';
                            }}
                        >
                            <div className="mb-8">
                                <Activity className="w-12 h-12 text-mscg-dark-gray" />
                            </div>
                            <h3 className="text-xl font-bold text-mscg-dark-gray mb-3">
                                Smarter Than Guesswork
                            </h3>
                            <p className="text-neutral-600 leading-relaxed">
                                We possess years of clinical experience and the best practices to provide care.
                            </p>
                        </div>

                        <div
                            className="feature-card-item p-8 rounded-2xl transition-all duration-300 bg-white"
                            style={{ border: '2px solid transparent' }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#d68800';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = 'transparent';
                                e.currentTarget.style.boxShadow = 'none';
                            }}
                        >
                            <div className="mb-8">
                                <User className="w-12 h-12 text-mscg-dark-gray" />
                            </div>
                            <h3 className="text-xl font-bold text-mscg-dark-gray mb-3">
                                Tailored Just for You
                            </h3>
                            <p className="text-neutral-600 leading-relaxed">
                                Every session adapts to your biofeedback, lifestyle and unique markers — not generic checklists.
                            </p>
                        </div>

                        <div
                            className="feature-card-item p-8 rounded-2xl transition-all duration-300 bg-white"
                            style={{ border: '2px solid transparent' }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#d68800';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = 'transparent';
                                e.currentTarget.style.boxShadow = 'none';
                            }}
                        >
                            <div className="mb-8">
                                <Calendar className="w-12 h-12 text-mscg-dark-gray" />
                            </div>
                            <h3 className="text-xl font-bold text-mscg-dark-gray mb-3">
                                Always-On Awareness
                            </h3>
                            <p className="text-neutral-600 leading-relaxed">
                                Each session tracks patterns and progress. You see what's changing — and why it matters.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Depression Hero Section */}
            <section className="depression-section relative h-screen min-h-[600px] flex items-center overflow-hidden">
                <video
                    className="absolute top-0 left-0 w-full h-[140%] object-cover"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="none"
                    poster="/images/Depressed.jpg"
                >
                    <source src="/images/HopeIsHere.mp4" type="video/mp4" />
                    {/* Fallback image if video doesn't load */}
                    <img
                        src="/images/Depressed.jpg"
                        alt="Depression support"
                        className="absolute top-0 left-0 w-full h-[140%] object-cover"
                    />
                </video>
                <div className="absolute inset-0 bg-black/40"></div>
                <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8">
                    <div className="max-w-4xl">
                        <h2 className="depression-title text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                            There's <span className='text-yellow-500'>Hope</span> for Every Tomorrow
                        </h2>
                        <p className="depression-description text-white leading-relaxed pb-12">
                            Depression dims the world around you, but it doesn’t define you.<br />
                            Let's help you move forward with courage, clarity, and renewed hope.
                        </p>
                        <button
                            onClick={openModal}
                            className="inline-flex items-center gap-3 bg-white text-black pl-8 pr-2 py-2 rounded-full font-bold text-lg transition-all duration-300 hover:scale-110 shadow-lg hover:shadow-xl group"
                        >
                            Book Now
                            <div className="w-10 h-10 rounded-full bg-[#d68800] text-white flex items-center justify-center transition-all duration-300 group-hover:bg-[#891719] group-hover:rotate-45">
                                <ArrowUpRight className="w-5 h-5" />
                            </div>
                        </button>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="faq-section py-24 bg-[#f5f5f5]">
                <div className="w-full px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-stretch">
                        {/* Left Column - Video Background with Crisis Card */}
                        <div className="faq-image relative min-h-[600px] rounded-3xl overflow-hidden shadow-xl">
                            <video
                                className="absolute inset-0 w-full h-full object-cover"
                                autoPlay
                                muted
                                loop
                                playsInline
                                preload="none"
                            >
                                <source src="/videos/faq-background.mp4" type="video/mp4" />
                            </video>

                            {/* Crisis Line Card Overlay */}
                            <div className="absolute bottom-8 left-8 right-8 bg-white rounded-2xl shadow-2xl p-6 sm:p-8 backdrop-blur-sm">
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-red-500 mb-2">Crisis Line</h3>
                                        <p className="text-black text-sm leading-relaxed">
                                            If you, or someone you know is in crisis, please seek help immediately by calling{' '}
                                            <a href="tel:1-844-493-8255" className="font-bold text-[#d68800] hover:underline">1-844-493-8255</a>
                                            {' '}or texting the word <span className="font-bold">TALK</span> to{' '}
                                            <a href="sms:38255" className="font-bold text-[#d68800] hover:underline">38255</a>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Content - Dark Background */}
                        <div className="faq-content bg-[#293039] p-10 rounded-3xl shadow-2xl flex flex-col justify-center">
                            <h2 className="text-4xl md:text-5xl font-bold text-white mb-10 tracking-tight">
                                Quick Answers
                            </h2>
                            <div className="space-y-0 border-t border-white/10">
                                {faqs.map((faq, index) => (
                                    <div key={index} className="faq-item border-b border-white/10">
                                        <button
                                            className="w-full py-6 flex items-center justify-between text-left group outline-none focus:outline-none"
                                            onClick={() => setOpenFaq(openFaq === index ? null : index)}
                                        >
                                            <span className={`text-xl font-medium transition-colors duration-300 ${openFaq === index ? 'text-[#d68800]' : 'text-white group-hover:text-[#d68800]'}`}>
                                                {faq.question}
                                            </span>
                                            <span className={`ml-4 flex-shrink-0 transition-colors duration-300 ${openFaq === index ? 'text-white' : 'text-[#d68800]'}`}>
                                                {openFaq === index ? <Minus className="w-6 h-6" /> : <Plus className="w-6 h-6" />}
                                            </span>
                                        </button>
                                        <div
                                            className={`overflow-hidden transition-all duration-500 ease-in-out ${openFaq === index ? 'max-h-[2000px] opacity-100 pb-6' : 'max-h-0 opacity-0'}`}
                                        >
                                            <div className="text-neutral-300 leading-relaxed text-lg">
                                                {faq.answer}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Newsletter Signup Section */}
            <section className="newsletter-section py-20 bg-[#293039] border-t border-white/10">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl md:text-4xl font-serif text-white mb-4">Subscribe to Our Newsletter</h2>
                    <p className="text-white/70 mb-10">Stay updated with our latest insights, tips, and resources for your wellness journey.</p>
                    <div className="bg-white/5 p-2 rounded-3xl shadow-xl overflow-hidden backdrop-blur-sm flex justify-center">
                        <iframe width="540" height="305" src="https://3ce33466.sibforms.com/serve/MUIFAHHCYDzuSichwdQ5qAygJbnX-LsOsV33RG7t5A26xeFSY_IZlKmK95cMaQHzwo_Fd_COUmYwdYG0_sy3lbmYwTHohKJYcOAY2SMJU1JjFCH9YJwqI83f0R9GVeT8eesHqPTw9Xu9IIPJSRhRIRC5MaqOwtZaoOXyMV01d5UneC5ueOj-UgscsHdrLQxNhQEsuKT40a6RwT3f" frameBorder="0" scrolling="auto" allowFullScreen style={{ display: 'block', margin: '0 auto', maxWidth: '100%' }} className="rounded-2xl bg-white"></iframe>
                    </div>
                </div>
            </section>

        </div >
    );
};
